import { usePage } from "@inertiajs/vue3";
import { computed } from "vue";

export function usePermissions() {
  // Access the current Inertia page
  const page = usePage();

  // Extract user roles from the page props
  const userRoles = computed(() => page.props.auth?.user?.roles?.map((role) => role.name) || []);

  // User type (admin, member, vendor, or rep)
  const userType = computed(() => page.props.auth?.user?.type);

  // Flatten permissions from all roles into a single array of permission names
  const userPermissions = computed(
    () => page.props.auth?.user?.roles?.flatMap((role) => role.permissions).map((perm) => perm.name) || []
  );

  // Policy overrides from the server
  const userPermissionOverride = computed(() => page.props?.can);

  // Check if user has a specific role
  const hasRole = (role) => userRoles.value.includes(role) || userType.value === role;

  // Check if user has any of the provided roles
  const hasAnyRole = (...roles) => {
    return roles.some((role) => hasRole(role));
  };

  // Check if user has a specific permission, considering overrides
  const hasPermission = (permission) => {
    // Super admin has all permissions
    if (hasRole("super admin")) {
      return true;
    }

    const override = userPermissionOverride.value;

    if (override && Object.prototype.hasOwnProperty.call(override, permission)) {
      return override[permission];
    }

    return userPermissions.value.includes(permission);
  };

  // Alias for checking permissions
  const can = (permission) => hasPermission(permission);

  // Alias for checking lack of permissions
  const cannot = (permission) => !hasPermission(permission);

  // Alias for checking roles, supports pipe-separated roles (e.g., 'admin|vendor')
  const is = (role) => {
    if (typeof role === "string" && role.includes("|")) {
      return role.split("|").some((r) => hasRole(r.trim()));
    }
    return hasRole(role);
  };

  // Alias for checking lack of roles
  const isnt = (role) => !is(role);

  return {
    userRoles,
    userPermissions,
    hasRole,
    hasAnyRole,
    hasPermission,
    can,
    cannot,
    is,
    isnt,
  };
}
